import { Badge } from "@/components/Badge.tsx";
import { LeftRight } from "@/components/LeftRight.tsx";
import { InfoTooltip } from "@/components/WTooltip.tsx";
import { WSelect, type ConfiguredWSelectProps } from "@/components/forms/WSelect.tsx";
import { WSelectWrapper } from "@/components/forms/WSelectWrapper.tsx";
import { BillingAccountIcon, ShortInfoSelect } from "@/modules/billing/BillingAccountInfo.tsx";
import { eBillingAccountQueryAtom } from "@/modules/billing/billingAccountQuery.ts";
import { getDefaultAccount, type EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans } from "@lingui/react/macro";
import type { BillingAccountId } from "@warrenio/api-spec/spec.oats.gen";

export interface BillingSelectProps extends ConfiguredWSelectProps<EBillingAccount, BillingAccountId> {
    allowClosedAccounts?: boolean;
    /** Only show title (no icon, auxiliary info on right) */
    compact?: boolean;
    isReferral?: boolean;
}

export function BillingSelectCustom({
    allowClosedAccounts,
    isItemDisabled,
    compact,
    isReferral,
    ...props
}: BillingSelectProps) {
    return (
        <WSelect
            isReferral={isReferral}
            aria-label="Billing account"
            dynamicDefault={(items) => getDefaultAccount(items)}
            getKey={(item) => item.id}
            renderUnknown={() => (
                <InfoTooltip text={<Trans>The billing account has been closed and is no longer available.</Trans>}>
                    <Trans>N/A</Trans>
                </InfoTooltip>
            )}
            getTextValue={(item) => item.title}
            searchItems
            {...props}
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            isItemDisabled={(item) => isItemDisabled?.(item) || (!allowClosedAccounts && !item.isOpen)}
        >
            {(item) => (
                <LeftRight
                    icon={!compact && <BillingAccountIcon account={item} />}
                    title={item.title}
                    right={
                        !compact && (
                            <>
                                {!!item.account.discount_percentage && (
                                    <>
                                        <Badge noDot color="success">
                                            -{item.account.discount_percentage}%
                                        </Badge>{" "}
                                    </>
                                )}
                                <ShortInfoSelect account={item} />
                            </>
                        )
                    }
                />
            )}
        </WSelect>
    );
}

export type BillingSelectLoaderProps = Omit<BillingSelectProps, "items">;

function BillingSelectLoader(props: BillingSelectLoaderProps) {
    const data = useSuspenseQueryAtom(eBillingAccountQueryAtom);
    const currentAccount = props.valueKey ? data.get(props.valueKey) : undefined;

    return <BillingSelectCustom value={currentAccount} items={[...data.values()]} {...props} />;
}

export function BillingSelect(props: BillingSelectLoaderProps) {
    return (
        <WSelectWrapper fallback={(renderProps) => <BillingSelectCustom {...props} {...renderProps} />}>
            <BillingSelectLoader {...props} />
        </WSelectWrapper>
    );
}
